import React, { useMemo } from "react";

import EmptyState from "../../components/EmptyState";
import PageHead from "../../components/PageHead";
import * as api from "./api";
import CreativeActions from "./CreativeActions";
import OfferPicker from "./OfferPicker";
import { Async, Kpi, KpiRow, Panel } from "./panels";
import { useEndpoint } from "./useOffer";

/**
 * Workstation · Overview (§4.1).
 *
 * "CIPHER'S UNDERSTANDING" IS DOMINANT BY LAYOUT, AND NOW REAL (H1)
 * ==================================================================
 * §4.1 makes it the dominant element and the Definition of Done asks for
 * exactly that. It used to render a designed empty state because no route
 * composed "what's working / not working / meaningful changes / learnings /
 * noteworthy" for an offer — `design/system/audits/workstation-tabs.md` §1.
 * `GET /api/offers/{offer_id}/understanding` (`backend/routers/offers.py:616`,
 * built by `backend/services/offer_understanding.py`) closes that gap: it
 * composes the five categories from data that already exists — performance
 * snapshots, campaign health status, predictions, operator decisions,
 * recommendations, vertical-level observations and hypotheses. No new data
 * collection, no LLM call, and a category with nothing to say returns an
 * empty array rather than a fabricated item.
 *
 * Still full size deliberately, per the same reasoning as before: shrinking it
 * would encode "minor panel" into the layout, and the categories are rendered
 * even when every one of them is empty — an honest "nothing to report" per
 * category (R6), not a hidden section and not a spinner that never resolves.
 *
 * WHAT IS REAL ON THIS TAB
 * ========================
 * "Cipher's Understanding", from `GET /api/offers/{offer_id}/understanding`.
 * The KPI layer, from `GET /api/performance/snapshots?offer_id=`. The creative
 * actions, reaching the handlers they already reach. Everything else §4.1
 * lists (Latest Learnings, Active Creative Snapshot, the drill strip) is out
 * of tonight's scope and is left unbuilt rather than half-built.
 */

/** Category label + copy for an empty category, in the order §4.1 lists them. */
const UNDERSTANDING_CATEGORIES = [
  {
    key: "working",
    label: "Working",
    emptyDetail: "Nothing is flagged as working for this offer right now.",
  },
  {
    key: "not_working",
    label: "Not working",
    emptyDetail: "Nothing is flagged as not working for this offer right now.",
  },
  {
    key: "changed_meaningfully",
    label: "Changed meaningfully",
    emptyDetail:
      "No metric moved more than 20% between the last 7 days and the 7 before, on enough spend to be meaningful.",
  },
  {
    key: "learned",
    label: "Learned",
    emptyDetail: "No resolved predictions, recorded decisions, or vertical observations yet.",
  },
  {
    key: "noteworthy",
    label: "Noteworthy",
    emptyDetail: "No open predictions, pending recommendations, or proposed hypotheses.",
  },
];

/** One item inside a category: its text, and a small indicator of its source. */
function UnderstandingItem({ item }) {
  return (
    <li className="ws-understanding-item">
      <span>{item.text}</span>
      {item.source ? (
        <span className="ws-tag ws-understanding-source" title="where this came from">
          {item.source}
        </span>
      ) : null}
    </li>
  );
}

/** One of the five categories: its items, or the honest empty sub-state. */
function UnderstandingCategory({ label, emptyDetail, items }) {
  return (
    <div className="ws-understanding-category">
      <h3 className="section-title">{label}</h3>
      {Array.isArray(items) && items.length > 0 ? (
        <ul className="ws-list">
          {items.map((item, i) => (
            <UnderstandingItem
              key={item.prediction_id || item.recommendation_id || item.observation_id ||
                item.hypothesis_id || item.decision_id || item.campaign_id || i}
              item={item}
            />
          ))}
        </ul>
      ) : (
        <EmptyState title="Nothing to report" detail={emptyDetail} />
      )}
    </div>
  );
}

/** The dominant panel's body: loading/error via `Async`, five categories once ready. */
function UnderstandingBody({ understanding }) {
  return (
    <Async
      result={understanding}
      deniedLabel="Cipher's Understanding requires a signed-in operator, and the server refused it. That is the gate working, not a fault."
    >
      {(data) => (
        <div className="ws-understanding-categories">
          {UNDERSTANDING_CATEGORIES.map((cat) => (
            <UnderstandingCategory
              key={cat.key}
              label={cat.label}
              emptyDetail={cat.emptyDetail}
              items={data?.[cat.key]}
            />
          ))}
        </div>
      )}
    </Async>
  );
}

/** The KPI layer, derived from the snapshot rows the endpoint returned.
 *
 * Summed in the client from rows the server already returns — this composes
 * nothing new on the backend and adds no endpoint. `null` where there is
 * nothing to sum, so the tile renders a dash rather than a zero: "no rows in
 * this window" and "zero spend" are different facts.
 */
function rollUp(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const sum = (key) =>
    rows.reduce((acc, r) => acc + (Number(r?.[key]) || 0), 0);

  const spend = sum("spend");
  const revenue = sum("revenue");
  const conversions = sum("conversions");
  const clicks = sum("clicks");
  const impressions = sum("impressions");

  return {
    rows: rows.length,
    spend,
    revenue,
    profit: revenue - spend,
    // Undefined rather than zero when the denominator is absent. A ROAS of 0.00
    // on no spend reads as a catastrophe; the truth is that it is not defined.
    roas: spend > 0 ? revenue / spend : null,
    cpa: conversions > 0 ? spend / conversions : null,
    conversions,
    ctr: impressions > 0 ? (clicks / impressions) * 100 : null,
  };
}

export default function Overview({ selection }) {
  const { offers, selected, selectedId, select, error } = selection;

  const snapshots = useEndpoint(
    () => api.getSnapshots(selectedId),
    [selectedId],
    { skip: !selectedId }
  );
  const sessions = useEndpoint(
    () => api.listCreativeSessions(selectedId),
    [selectedId],
    { skip: !selectedId }
  );
  const understanding = useEndpoint(
    () => api.getOfferUnderstanding(selectedId),
    [selectedId],
    { skip: !selectedId }
  );

  const kpi = useMemo(
    () => (snapshots.state === "ready" ? rollUp(snapshots.data) : null),
    [snapshots.state, snapshots.data]
  );

  return (
    <>
      <PageHead
        title={selected?.name || "Workstation"}
        subtitle="One offer, end to end — what Cipher understands about it and what it is doing."
        // WITH the offer, not before it. Both actions are meaningless without
        // one — "New Creative Session" for what? — and a permanently disabled
        // pair of buttons is furniture that teaches the operator to ignore that
        // corner of the header. It also keeps the area renderable inside the
        // shell with nothing selected, which is how the shell's own tests
        // exercise every area.
        actions={
          selectedId ? (
            <CreativeActions offerId={selectedId} sessions={sessions} />
          ) : null
        }
      />

      <OfferPicker
        offers={offers}
        selectedId={selectedId}
        onSelect={select}
        error={error}
      />

      {!selectedId ? (
        <div className="state ws-state" role="status">
          Select an offer to see what Cipher understands about it.
        </div>
      ) : (
        <div className="ws-overview-grid">
          {/* DOMINANT. First in source order and spanning the full grid, so it
              is the dominant element for a screen reader and a sighted reader
              alike — layout dominance that exists only in CSS is dominance only
              for people who can see it. */}
          <Panel
            title="Cipher's Understanding"
            subtitle="What is working, what is not, what changed, and what it means for this offer."
            dominant
          >
            <UnderstandingBody understanding={understanding} />
          </Panel>

          <Panel
            title="Performance"
            subtitle="Summed from the snapshots this offer's campaigns wrote."
          >
            <Async
              result={snapshots}
              empty="No performance snapshots for this offer yet. The endpoint answered; it had nothing in the window."
            >
              {() =>
                kpi === null ? (
                  <div className="state ws-state" role="status">
                    No performance snapshots for this offer yet.
                  </div>
                ) : (
                  <>
                    <KpiRow>
                      <Kpi label="Spend" value={kpi.spend} format="currency" />
                      <Kpi label="Revenue" value={kpi.revenue} format="currency" />
                      <Kpi label="Profit" value={kpi.profit} format="currency" />
                      <Kpi
                        label="ROAS"
                        value={kpi.roas}
                        format="ratio"
                        note={kpi.roas === null ? "undefined — no spend" : null}
                      />
                    </KpiRow>
                    <KpiRow>
                      <Kpi label="Conversions" value={kpi.conversions} />
                      <Kpi
                        label="CPA"
                        value={kpi.cpa}
                        format="currency"
                        note={
                          kpi.cpa === null ? "undefined — no conversions" : null
                        }
                      />
                      <Kpi label="CTR" value={kpi.ctr} format="percent" />
                      <Kpi label="Snapshots" value={kpi.rows} />
                    </KpiRow>
                  </>
                )
              }
            </Async>
          </Panel>
        </div>
      )}
    </>
  );
}
